import { isDeploymentRecoveryEnabled } from "@/lib/deploymentRecoveryEnvironment";

const NEXT_STATIC_ASSET_PATTERN = /\/_next\/static\/(?:chunks|css)\//i;
const NEXT_STATIC_CHUNK_PATTERN = /\/_next\/static\/chunks\//i;
const NEXT_STATIC_CSS_PATTERN = /\/_next\/static\/css\//i;
const EXPLICIT_CHUNK_ERROR_PATTERNS = [
  /ChunkLoadError/i,
  /Loading chunk [\w-]+ failed/i,
  /Loading CSS chunk [\w-]+ failed/i,
  /CSS chunk load failed/i,
  /module factory is not available/i,
];
const NEXT_STATIC_MODULE_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /Error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /Expected a JavaScript module script/i,
  /MIME type .*?text\/html/i,
];
const NEXT_STATIC_CSS_ERROR_PATTERNS = [/Unable to preload CSS/i];
const GENERIC_NON_CHUNK_FAILURE_PATTERNS = [
  /\bAbortError\b/i,
  /\baborted\b/i,
  /\bcancelled\b/i,
  /\bcanceled\b/i,
  /^Load failed$/i,
  /^Failed to fetch$/i,
  /\bHydration failed\b/i,
];
const NON_APP_ASSET_PATTERN =
  /\/(?:api|business-photos|business-gallery|listing-photos|profile-photos|avatars)\//i;
const HTTP_API_ERROR_PATTERNS = [
  /\b(?:401|403|404)\b/,
  /\bUnauthorized\b/i,
  /\bForbidden\b/i,
  /\bNot Found\b/i,
  /\/api\//i,
];

function collectErrorText(value: unknown, seen = new Set<unknown>()): string {
  if (!value || seen.has(value)) return "";
  seen.add(value);

  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);

  if (value instanceof Error) {
    return [value.name, value.message, value.stack].filter(Boolean).join("\n");
  }

  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    return [
      record.name,
      record.message,
      record.stack,
      record.reason ? collectErrorText(record.reason, seen) : "",
      record.error ? collectErrorText(record.error, seen) : "",
      record.cause ? collectErrorText(record.cause, seen) : "",
      record.target ? collectErrorText(record.target, seen) : "",
      record.srcElement ? collectErrorText(record.srcElement, seen) : "",
      typeof record.href === "string" ? record.href : "",
      typeof record.src === "string" ? record.src : "",
    ]
      .filter(Boolean)
      .join("\n");
  }

  return "";
}

function collectErrorStatus(value: unknown, seen = new Set<unknown>()): number | null {
  if (!value || seen.has(value)) return null;
  seen.add(value);

  if (typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const status = record.status ?? record.statusCode;
  if (typeof status === "number") return status;
  if (typeof status === "string") {
    const parsed = Number.parseInt(status, 10);
    if (Number.isFinite(parsed)) return parsed;
  }
  return (
    collectErrorStatus(record.reason, seen) ??
    collectErrorStatus(record.error, seen) ??
    collectErrorStatus(record.cause, seen) ??
    collectErrorStatus(record.target, seen) ??
    collectErrorStatus(record.srcElement, seen)
  );
}

function isHttpApiErrorText(text: string): boolean {
  return HTTP_API_ERROR_PATTERNS.some((pattern) => pattern.test(text));
}

function hasGenericNonChunkFailure(text: string): boolean {
  return GENERIC_NON_CHUNK_FAILURE_PATTERNS.some((pattern) => pattern.test(text));
}

function hasExplicitChunkSignature(text: string): boolean {
  return EXPLICIT_CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(text));
}

function hasNextStaticModuleSignature(text: string): boolean {
  return (
    NEXT_STATIC_CHUNK_PATTERN.test(text) &&
    NEXT_STATIC_MODULE_ERROR_PATTERNS.some((pattern) => pattern.test(text))
  );
}

function hasNextStaticCssSignature(text: string): boolean {
  return (
    NEXT_STATIC_CSS_PATTERN.test(text) &&
    NEXT_STATIC_CSS_ERROR_PATTERNS.some((pattern) => pattern.test(text))
  );
}

export function isChunkLoadError(error: unknown): boolean {
  const text = collectErrorText(error);
  if (!text) return false;
  const hasNextStaticAsset = NEXT_STATIC_ASSET_PATTERN.test(text);
  const status = collectErrorStatus(error);
  if (!hasNextStaticAsset && status && status >= 400 && status < 500) {
    return false;
  }
  if (!hasNextStaticAsset && NON_APP_ASSET_PATTERN.test(text)) {
    return false;
  }
  if (!hasNextStaticAsset && isHttpApiErrorText(text)) {
    return false;
  }
  if (!hasNextStaticAsset && hasGenericNonChunkFailure(text)) {
    return false;
  }
  if (hasNextStaticAsset && status && status >= 400) {
    return true;
  }
  if (hasExplicitChunkSignature(text)) {
    return true;
  }
  return hasNextStaticModuleSignature(text) || hasNextStaticCssSignature(text);
}

export type PwaChunkRecoveryAction =
  | "ignore"
  | "prefetch_ignored"
  | "deferred"
  | "failed"
  | "refreshing";

export type PwaChunkRecoveryDecision = {
  action: PwaChunkRecoveryAction;
  automaticNavigation: boolean;
  diagnostics: {
    chunkDpl: string;
    chunkPath: string | null;
    requestKind: "active_route" | "prefetch" | "unknown";
    responseCategory: string;
  };
};

const NEXT_ASSET_URL_PATTERN =
  /(?:https?:\/\/[^\s"'<>]+)?\/_next\/static\/(?:chunks|css)\/[^\s"'<>]+/i;

function nestedMetadata(
  value: unknown,
  keys: string[],
  seen = new Set<unknown>()
): unknown {
  if (!value || typeof value !== "object" || seen.has(value)) return undefined;
  seen.add(value);
  const record = value as Record<string, unknown>;
  for (const key of keys) {
    if (record[key] !== undefined) return record[key];
  }
  return (
    nestedMetadata(record.reason, keys, seen) ??
    nestedMetadata(record.error, keys, seen) ??
    nestedMetadata(record.cause, keys, seen) ??
    nestedMetadata(record.target, keys, seen) ??
    nestedMetadata(record.srcElement, keys, seen)
  );
}

function sanitizeCategory(value: unknown): string {
  if (typeof value !== "string") return "unknown";
  const normalized = value.trim().slice(0, 80);
  return /^[a-zA-Z0-9._:-]+$/.test(normalized) ? normalized : "unknown";
}

function extractChunkUrl(error: unknown, origin: string): URL | null {
  const text = collectErrorText(error);
  const direct = nestedMetadata(error, ["src", "href", "chunkUrl"]);
  const candidate =
    (typeof direct === "string" && direct.includes("/_next/static/")
      ? direct
      : text.match(NEXT_ASSET_URL_PATTERN)?.[0]) || "";
  if (!candidate) return null;
  try {
    const parsed = new URL(candidate.replace(/[)\]},.;]+$/, ""), origin);
    return parsed.pathname.startsWith("/_next/static/") ? parsed : null;
  } catch {
    return null;
  }
}

export function getPwaChunkFailureDetails(
  error: unknown,
  origin = "https://invalid.local"
): PwaChunkRecoveryDecision["diagnostics"] & {
  chunkUrl: string;
  prefetch: boolean;
  failure: true;
} {
  const text = collectErrorText(error);
  const chunkUrl = extractChunkUrl(error, origin);
  const explicitPrefetch = nestedMetadata(error, [
    "prefetch",
    "isPrefetch",
    "nextRouterPrefetch",
  ]);
  const requestType = nestedMetadata(error, [
    "requestType",
    "initiatorType",
    "navigationType",
    "rel",
    "purpose",
  ]);
  const prefetch =
    explicitPrefetch === true ||
    (typeof requestType === "string" && /prefetch|modulepreload/i.test(requestType)) ||
    /failed to prefetch/i.test(text);
  const activeRoute =
    nestedMetadata(error, ["activeRoute", "required", "isNavigation"]) === true ||
    (typeof requestType === "string" && /navigation|route/i.test(requestType));
  const status = collectErrorStatus(error);
  const contentType = nestedMetadata(error, ["contentType", "mimeType"]);
  const redirected = nestedMetadata(error, ["redirected"]) === true;
  const responseCategory =
    status === 401
      ? "http_401"
      : status === 403
        ? "http_403"
        : status === 404
          ? "http_404"
          : status && status >= 400
            ? "http_error"
            : redirected
              ? "redirect"
              : typeof contentType === "string" && /text\/html/i.test(contentType)
                ? "html"
                : /load failed|failed to fetch|network/i.test(text)
                  ? "network"
                  : "unknown";

  const chunkDpl = sanitizeCategory(chunkUrl?.searchParams.get("dpl"));
  const safeChunkUrl = chunkUrl
    ? `${chunkUrl.origin}${chunkUrl.pathname}${
        chunkDpl === "unknown" ? "" : `?dpl=${encodeURIComponent(chunkDpl)}`
      }`
    : "";
  return {
    chunkDpl,
    chunkPath: chunkUrl?.pathname.slice(0, 240) || null,
    chunkUrl: safeChunkUrl,
    failure: true,
    prefetch,
    requestKind: prefetch ? "prefetch" : activeRoute ? "active_route" : "unknown",
    responseCategory,
  };
}

function fallbackDecision(
  action: PwaChunkRecoveryAction,
  diagnostics: PwaChunkRecoveryDecision["diagnostics"]
): PwaChunkRecoveryDecision {
  return { action, automaticNavigation: false, diagnostics };
}

export async function coordinatePwaChunkRecovery(
  error: unknown,
  {
    origin = typeof window === "undefined" ? "https://invalid.local" : window.location.origin,
  }: { origin?: string } = {}
): Promise<PwaChunkRecoveryDecision> {
  const details = getPwaChunkFailureDetails(error, origin);
  if (!isChunkLoadError(error)) return fallbackDecision("ignore", details);
  if (details.prefetch) return fallbackDecision("prefetch_ignored", details);
  if (!isDeploymentRecoveryEnabled()) return fallbackDecision("ignore", details);
  if (typeof window === "undefined") return fallbackDecision("failed", details);

  const coordinator = (
    window as typeof window & {
      __ybPwaStartup?: {
        coordinateDeploymentRecovery?: (
          input: typeof details
        ) => Promise<{ action?: PwaChunkRecoveryAction; automaticNavigation?: boolean }>;
      };
    }
  ).__ybPwaStartup?.coordinateDeploymentRecovery;
  if (typeof coordinator !== "function") {
    return fallbackDecision("failed", details);
  }
  const result = await coordinator(details);
  return {
    action: result?.action || "failed",
    automaticNavigation: result?.automaticNavigation === true,
    diagnostics: details,
  };
}

export function getPrivacySafeChunkRecoveryError(
  details: PwaChunkRecoveryDecision["diagnostics"]
) {
  return {
    name: "ChunkLoadError",
    message: details.chunkPath
      ? `Chunk asset failed: ${details.chunkPath}`
      : "A required application chunk failed.",
  };
}
